import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './NotificationCentre.css';
import CreateNotificationModal from './CreateNotificationModal';
import NotificationDetailModal from './NotificationDetailModal';

const CLASS_PALETTE = [
  '#9E2F63', // Wine / Berry
  '#2E7D5B', // Emerald / Forest
  '#B5651D', // Amber / Rust
  '#5B4A9E', // Purple / Indigo
  '#0B5CAD', // Cobalt / Blue
  '#C05621'  // Burnt Orange
];

/**
 * Deterministically pick an audience colour for a class ID
 */
export function getAudienceColor(visibility, scheduleId) {
  if (visibility === 'ALL') {
    return '#4A78B5';
  }
  const id = parseInt(scheduleId, 10) || 0;
  return CLASS_PALETTE[Math.abs(id) % CLASS_PALETTE.length];
}

/**
 * Format date group heading in Asia/Kuala_Lumpur timezone
 */
export function formatGroupDate(dateInput) {
  if (!dateInput) return 'Earlier';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'Earlier';

  try {
    const now = new Date();
    const klFormat = (d, opts) =>
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kuala_Lumpur', ...opts }).format(d);

    const dYear = parseInt(klFormat(date, { year: 'numeric' }), 10);
    const dMonth = parseInt(klFormat(date, { month: 'numeric' }), 10);
    const dDay = parseInt(klFormat(date, { day: 'numeric' }), 10);

    const nowYear = parseInt(klFormat(now, { year: 'numeric' }), 10);
    const nowMonth = parseInt(klFormat(now, { month: 'numeric' }), 10);
    const nowDay = parseInt(klFormat(now, { day: 'numeric' }), 10);

    const dateUtc = Date.UTC(dYear, dMonth - 1, dDay);
    const nowUtc = Date.UTC(nowYear, nowMonth - 1, nowDay);
    const diffDays = Math.round((nowUtc - dateUtc) / (1000 * 60 * 60 * 24));

    const dayOfMonth = klFormat(date, { day: 'numeric' });
    const monthName = klFormat(date, { month: 'long' });
    const weekday = klFormat(date, { weekday: 'long' });

    if (diffDays === 0) {
      return `Today, ${dayOfMonth} ${monthName}`;
    }
    if (diffDays === 1) {
      return `Yesterday, ${dayOfMonth} ${monthName}`;
    }
    if (dYear !== nowYear) {
      return `${weekday}, ${dayOfMonth} ${monthName} ${dYear}`;
    }
    return `${weekday}, ${dayOfMonth} ${monthName}`;
  } catch (e) {
    return 'Recent';
  }
}

/**
 * Format 24-hour posted time in Asia/Kuala_Lumpur
 */
export function formatPostTime(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kuala_Lumpur',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  } catch (e) {
    return '';
  }
}

/**
 * Helper to group an array of notification items by local date
 */
function groupItemsByDate(items) {
  const groups = [];
  const dateMap = new Map();

  items.forEach(item => {
    const groupKey = formatGroupDate(item.createdAt);
    if (!dateMap.has(groupKey)) {
      const newGroup = { heading: groupKey, items: [] };
      dateMap.set(groupKey, newGroup);
      groups.push(newGroup);
    }
    dateMap.get(groupKey).items.push(item);
  });

  return groups;
}

const NotificationCentre = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toast, setToast] = useState(null);

  // Stacked ticked notifications area state (collapsed by default)
  const [isStackedExpanded, setIsStackedExpanded] = useState(false);

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Determine user role
  const rawRole = (user?.role || user?.Role || '').trim().toLowerCase();
  const isAdmin = rawRole.includes('admin') || rawRole.includes('editor') || rawRole.includes('super');
  const userId = user?.id || user?.adminId || user?.trainerId || user?.learnerId || '';

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Auth headers helper
  const getAuthHeaders = useCallback(() => {
    return {
      'x-user-role': user?.role || user?.Role || 'Learner',
      'x-admin-role': user?.role || user?.Role || 'Learner',
      'x-user-id': String(userId)
    };
  }, [user, userId]);

  // Fetch initial notifications (limit=5, offset=0)
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/notifications?limit=5&offset=0', {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setNotifications(data.data || []);
        setUnreadCount(data.unreadCount || 0);
        setHasMore(Boolean(data.hasMore));
      } else {
        console.warn('Failed to load notifications:', data.message);
      }
    } catch (err) {
      console.error('Network error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Load more notifications (pagination)
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const currentOffset = notifications.length;
      const res = await fetch(`/api/notifications?limit=5&offset=${currentOffset}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        const nextItems = data.data || [];
        setNotifications(prev => [...prev, ...nextItems]);
        setUnreadCount(data.unreadCount || 0);
        setHasMore(Boolean(data.hasMore));
      }
    } catch (err) {
      console.error('Error loading more notifications:', err);
      showToast('error', 'Failed to load more notifications.');
    } finally {
      setLoadingMore(false);
    }
  };

  // Toggle read status optimistically
  const handleToggleRead = async (notificationId, currentIsRead) => {
    const nextReadState = !currentIsRead;

    // Optimistic UI update
    setNotifications(prev =>
      prev.map(item =>
        item.notificationId === notificationId ? { ...item, isRead: nextReadState } : item
      )
    );
    setUnreadCount(prev => Math.max(0, nextReadState ? prev - 1 : prev + 1));

    try {
      const res = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ read: nextReadState })
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success') {
        // Rollback on failure
        setNotifications(prev =>
          prev.map(item =>
            item.notificationId === notificationId ? { ...item, isRead: currentIsRead } : item
          )
        );
        setUnreadCount(prev => Math.max(0, currentIsRead ? prev - 1 : prev + 1));
        showToast('error', data.message || 'Failed to update read status.');
      }
    } catch (err) {
      // Rollback on network error
      setNotifications(prev =>
        prev.map(item =>
          item.notificationId === notificationId ? { ...item, isRead: currentIsRead } : item
        )
      );
      setUnreadCount(prev => Math.max(0, currentIsRead ? prev - 1 : prev + 1));
      showToast('error', 'Network error. Please try again.');
    }
  };

  // Click title: open detail modal and automatically mark as read
  const handleOpenDetail = (notif) => {
    setSelectedDetail(notif);
    if (!notif.isRead) {
      handleToggleRead(notif.notificationId, false);
    }
  };

  // Callback when admin publishes new notification
  const handleNotificationCreated = () => {
    fetchNotifications();
  };

  // Split notifications into active (unticked/unread) and completed (ticked/read)
  const untickedNotifications = useMemo(() =>
    notifications.filter(item => !item.isRead),
    [notifications]
  );

  const tickedNotifications = useMemo(() =>
    notifications.filter(item => Boolean(item.isRead)),
    [notifications]
  );

  // Group unticked notifications by date
  const groupedUnticked = useMemo(() =>
    groupItemsByDate(untickedNotifications),
    [untickedNotifications]
  );

  // Group ticked notifications by date
  const groupedTicked = useMemo(() =>
    groupItemsByDate(tickedNotifications),
    [tickedNotifications]
  );

  // Reusable row renderer
  const renderNotificationRow = (item) => {
    const audienceColor = getAudienceColor(item.visibility, item.courseScheduleId);
    const isRead = Boolean(item.isRead);
    const contextLine = item.visibility === 'ALL'
      ? 'ANNOUNCEMENT · ALL LEARNERS'
      : `ANNOUNCEMENT · ${item.audienceLabel}`;

    return (
      <article
        key={item.notificationId}
        className={`nc-row ${isRead ? 'is-read' : 'is-unread'}`}
        style={{ borderTopColor: audienceColor }}
        data-testid={`notification-row-${item.notificationId}`}
      >
        {/* 1. Audience Tile */}
        <div className="nc-audience-tile">
          <div className="nc-tile-top" style={{ borderColor: audienceColor }}>
            <span className="nc-tile-top-text">
              {item.audienceLabel}
            </span>
          </div>
          <div className="nc-tile-bottom" style={{ backgroundColor: audienceColor }}>
            <span>{item.audienceLabel}</span>
          </div>
        </div>

        {/* 2. Checkbox */}
        <div className="nc-checkbox-wrapper">
          <button
            type="button"
            className={`nc-checkbox ${isRead ? 'checked' : ''}`}
            style={{ borderColor: audienceColor }}
            onClick={() => handleToggleRead(item.notificationId, isRead)}
            aria-label={isRead ? 'Mark as unread' : 'Mark as read'}
            data-testid={`checkbox-read-${item.notificationId}`}
          >
            {isRead && <span className="nc-checkbox-tick">✓</span>}
          </button>
        </div>

        {/* 3. Type Icon (Megaphone glyph in audience colour) */}
        <div
          className="nc-type-icon"
          style={{ backgroundColor: audienceColor }}
          aria-hidden="true"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 11 18-5v12L3 13v-2z" />
            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
          </svg>
        </div>

        {/* 4. Text Block */}
        <div className="nc-text-block">
          <span className="nc-context-line">{contextLine}</span>
          <button
            type="button"
            className={`nc-item-title ${isRead ? 'read' : 'unread'}`}
            onClick={() => handleOpenDetail(item)}
            data-testid={`title-link-${item.notificationId}`}
          >
            {item.title}
          </button>
        </div>

        {/* 5. Right-Aligned Metadata */}
        <div className="nc-meta-block">
          <span className="nc-publisher">
            By {item.createdByName || 'Administrator'}
          </span>
          <span className="nc-posted-time">
            POSTED: {formatPostTime(item.createdAt)}
          </span>
        </div>
      </article>
    );
  };

  return (
    <section className="nc-container" data-testid="notification-centre-section" aria-label="Notification Centre">
      {/* 4.1 Section Header ("Notifications" Title) */}
      <div className="nc-header-row">
        <div className="nc-header-left">
          <h2 className="nc-header-title">Notifications</h2>
          {unreadCount > 0 && (
            <span className="nc-unread-badge" data-testid="nc-unread-badge">
              {unreadCount} Unread
            </span>
          )}
        </div>

        {/* FR-6: Admin only Create Notification button */}
        {isAdmin && (
          <button
            type="button"
            className="nc-btn-create"
            onClick={() => setIsCreateOpen(true)}
            data-testid="btn-open-create-notification"
          >
            <span>+ Create Notification</span>
          </button>
        )}
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="nc-empty-state" data-testid="nc-loading">
          Loading notifications...
        </div>
      ) : notifications.length === 0 ? (
        /* FR-13: Empty State */
        <div className="nc-empty-state" data-testid="nc-empty-state">
          No notifications yet.
        </div>
      ) : (
        <>
          {/* Ticked Notifications Area placed just right after the "Notifications" title */}
          {tickedNotifications.length > 0 && (
            <section
              className="nc-stacked-area"
              data-testid="ticked-notifications-area"
              aria-label="Ticked notifications storage area"
            >
              <div
                className={`nc-stack-card ${isStackedExpanded ? 'expanded' : ''}`}
                onClick={() => setIsStackedExpanded(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsStackedExpanded(prev => !prev);
                  }
                }}
                aria-expanded={isStackedExpanded}
                aria-controls="nc-ticked-notifications-list"
                data-testid="ticked-notifications-stack-card"
              >
                <div className="nc-stack-card-left">
                  <div className="nc-stack-icon-wrapper" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                  </div>
                  <div className="nc-stack-title-group">
                    <span className="nc-stack-title">Ticked Notifications</span>
                    <span className="nc-stack-badge" data-testid="ticked-count-badge">
                      {tickedNotifications.length}
                    </span>
                  </div>
                </div>

                <div className="nc-stack-card-right">
                  <span className="nc-stack-hint">
                    {isStackedExpanded ? 'Click to collapse' : 'Stacked · Click to expand'}
                  </span>
                  <span className={`nc-stack-chevron ${isStackedExpanded ? 'rotated' : ''}`} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Expanded list showing all ticked notifications */}
              {isStackedExpanded && (
                <div
                  id="nc-ticked-notifications-list"
                  className="nc-stacked-expanded-list"
                  data-testid="ticked-notifications-expanded"
                >
                  {groupedTicked.map(group => (
                    <div key={`ticked-${group.heading}`} className="nc-date-group">
                      <h4 className="nc-date-heading nc-ticked-date-heading">{group.heading}</h4>
                      <div className="nc-rows-list">
                        {group.items.map(item => renderNotificationRow(item))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Active / Unticked Notifications Area */}
          {untickedNotifications.length === 0 ? (
            <div className="nc-all-caught-up" data-testid="nc-all-caught-up">
              <span className="nc-caught-up-icon">✓</span>
              <span>All caught up! No unread notifications.</span>
            </div>
          ) : (
            <div className="nc-groups-wrapper" data-testid="nc-active-groups-wrapper">
              {groupedUnticked.map(group => (
                <div key={group.heading} className="nc-date-group">
                  <h3 className="nc-date-heading">{group.heading}</h3>
                  <div className="nc-rows-list">
                    {group.items.map(item => renderNotificationRow(item))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FR-12: Pagination Show more control */}
          {hasMore && (
            <div className="nc-show-more-wrapper">
              <button
                type="button"
                className="nc-btn-show-more"
                onClick={handleLoadMore}
                disabled={loadingMore}
                data-testid="btn-show-more"
              >
                {loadingMore ? 'Loading more...' : 'Show more'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Admin Create Modal */}
      {isAdmin && (
        <CreateNotificationModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={handleNotificationCreated}
          user={user}
          showToast={showToast}
        />
      )}

      {/* Detail Modal */}
      {selectedDetail && (
        <NotificationDetailModal
          notification={selectedDetail}
          onClose={() => setSelectedDetail(null)}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <aside className={`nc-toast ${toast.type}`} role="alert" data-testid="nc-toast-message">
          <span>{toast.msg}</span>
        </aside>
      )}
    </section>
  );
};

export default NotificationCentre;
